import { useCallback, FC } from 'react';
import { Controller } from 'react-hook-form';
import cn from 'clsx';

import { makeStyles } from '@material-ui/styles';
import {
    Input,
    Select,
    Textarea,
    UserIconWithUpload,
    UserInfoHeader,
} from '@bizzabo/design-system';
import { config, useUpdateCurrentUser } from '@bizzabo/entities';

import { useCurrentUserContext } from '../../context/CurrentUserProvider';
import { useUserProfileViewContext, ViewType } from '../../context/UserProfileViewProvider';
import { useCountries } from '../../helpers/countries';
import { useEditForm, useSummaryDynamicMaxLength, useProfileAnalytics } from '../../hooks';
import HideProfileToggle from '../HideProfileToggle';
import FormControls from '../FormControls';
import CountryStateDropdown from './CountryStateDropdown';

const EditView: FC = () => {
    const { user } = useCurrentUserContext();
    const { modalView, setViewType } = useUserProfileViewContext();

    const classes = useStyles();

    const { sendProfileSaved } = useProfileAnalytics();

    const updateCurrentUser = useUpdateCurrentUser();
    const onEditSuccess = useCallback(() => {
        setViewType(ViewType.READ);
        sendProfileSaved();
    }, [user]);

    const { register, control, errors, onSubmit, setValue, isDirty } = useEditForm(
        user,
        onEditSuccess
    );

    const countries = useCountries(user?.accountId, user?.eventId);
    const maxLength = useSummaryDynamicMaxLength(user?.summary);

    return (
        <form onSubmit={onSubmit} className={cn({ [classes.wrapper]: modalView })} noValidate>
            <div className={classes.header}>
                <Controller
                    name="photoUrl"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <UserIconWithUpload
                            photoUrl={user.photoUrl}
                            onChange={(photoUrl: string) => {
                                onChange(photoUrl);
                                updateCurrentUser({
                                    eventId: user?.eventId,
                                    userProfileId: user?.userProfileId,
                                    payload: { photoUrl },
                                });
                            }}
                            value={value}
                            config={config}
                        />
                    )}
                />
                <UserInfoHeader
                    user={user}
                    hideUserIcon
                    titleVariant="t71"
                    descriptionVariant="t81"
                />
            </div>
            <div className={cn(classes.main, { [classes.modalMain]: modalView })}>
                <Input
                    label="First name"
                    error={errors.firstName?.message}
                    {...register('firstName')}
                />
                <Input
                    label="Last name"
                    error={errors.lastName?.message}
                    {...register('lastName')}
                />
                <Input
                    label="LinkedIn profile"
                    error={errors.linkedinId?.message}
                    {...register('linkedinId')}
                />
                <Input
                    label="Twitter handle"
                    error={errors.twitterHandle?.message}
                    {...register('twitterHandle')}
                />
                <Input label="Company" {...register('company')} error={errors.company?.message} />
                <Input label="Company website" {...register('web')} error={errors.web?.message} />
                <div className={classes.section}>
                    <Input label="Role" {...register('title')} error={errors.title?.message} />
                </div>
                <div className={classes.section}>
                    <Textarea label="About me" maxLength={maxLength} {...register('summary')} />
                </div>
                <Controller
                    name="country"
                    control={control}
                    render={({ field: { ref: _ref, ...selectProps } }) => (
                        <Select label="Country" options={countries} {...selectProps} />
                    )}
                />
                <CountryStateDropdown control={control} setValue={setValue} />
                <div className={cn(classes.section, classes.toggle)}>
                    <HideProfileToggle
                        eventId={user.eventId}
                        userProfileId={user.userProfileId}
                        isHidden={user.isHidden}
                    />
                </div>
            </div>
            <FormControls disableSave={!isDirty} />
        </form>
    );
};

const useStyles = makeStyles(
    theme => ({
        wrapper: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            padding: '0 30px 30px',
        },
        main: {
            display: 'block',

            '& > *': {
                marginBottom: 14,
            },
        },
        modalMain: {
            paddingLeft: 1,
            paddingRight: 1,
            marginLeft: -1,
            marginRight: -1,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gridColumnGap: 8,
            gridRowGap: 12,
            overflowY: 'auto',
            marginBottom: 20,

            '& > *': {
                marginBottom: 0,
            },
        },
        header: {
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',

            '& > div:first-of-type': {
                marginRight: 12,

                [theme.breakpoints.up('sm')]: {
                    marginRight: 16,
                },
            },
        },
        section: {
            gridColumn: '1 / 3',
        },
        toggle: {
            marginTop: 12,
        },
    }),
    { classNamePrefix: 'people-user-info-edit-view' }
);

export default EditView;
